import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, distinct, map, mergeMap, pluck, switchMap } from 'rxjs/operators';

import { AlertActionsUnion, SuccessAlert } from '@shared/alert';
import { Attachment } from '@shared/models';
import { AttachmentParams, AttachmentService } from '@shared/services';

import {
  attachmentsCreate,
  attachmentsCreateFailure,
  attachmentsCreateSuccess,
  attachmentsDelete,
  attachmentsDeleteFailure,
  attachmentsDeleteSuccess,
  attachmentsGet,
  attachmentsGetFailure,
  attachmentsGetLatestCoverLetter,
  attachmentsGetLatestCoverLetterFailure,
  attachmentsGetLatestCoverLetterSuccess,
  attachmentsGetLatestResume,
  attachmentsGetLatestResumeFailure,
  attachmentsGetLatestResumeSuccess,
  attachmentsGetResumes,
  attachmentsGetResumesFailure,
  attachmentsGetResumesSuccess,
  attachmentsGetSuccess,
  attachmentsLoad,
  attachmentsLoadFailure,
  attachmentsLoadSuccess,
} from './attachments.actions';
import { Action } from '@ngrx/store';

@Injectable()
export class AttachmentsEffects {
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsCreate),
      pluck('payload'),
      switchMap(
        ({
          file,
          params,
          candidateId,
        }: {
          file: File;
          params: Partial<AttachmentParams>;
          candidateId: number;
        }) =>
          this.attachmentService.save(file, params).pipe(
            map((attachment: Attachment) =>
              attachmentsCreateSuccess({
                payload: {
                  ...attachment,
                  candidateId,
                },
              }),
            ),
            catchError((error) => of(attachmentsCreateFailure({ error }))),
          ),
      ),
    ),
  );

  createSuccess$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsCreateSuccess),
      map(() => new SuccessAlert('File has been uploaded.')),
    ),
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsDelete),
      pluck('attachmentId'),
      switchMap((id: number) =>
        this.attachmentService.delete({ id }).pipe(
          map((attachment: Attachment) =>
            attachmentsDeleteSuccess({
              payload: attachment.id,
            }),
          ),
          catchError((error) => of(attachmentsDeleteFailure({ error }))),
        ),
      ),
    ),
  );

  createSuccessTriggersLoad$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsCreateSuccess),
      pluck('payload'),
      map(({ associationType, associateModuleId, associationModule }: Attachment) =>
        attachmentsLoad({
          payload: {
            associateModuleId,
            associationModule,
            associationType,
          },
        }),
      ),
    ),
  );

  load$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsLoad),
      pluck('payload'),
      mergeMap((payload: Partial<AttachmentParams>) =>
        this.attachmentService.list(payload).pipe(
          map((response) => attachmentsLoadSuccess({ payload: response })),
          catchError((error) => of(attachmentsLoadFailure({ error }))),
        ),
      ),
    ),
  );

  loadLatestResume: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsGetLatestResume),
      pluck('payload'),
      distinct(
        (payload: { candidateId: number; onlyPdfFiles: boolean }) =>
          payload.candidateId + (payload.onlyPdfFiles ? '-onlyPdf' : '-all'),
      ),
      mergeMap((payload: { candidateId: number; onlyPdfFiles: boolean }) =>
        this.attachmentService.latestResume(payload.candidateId, payload.onlyPdfFiles).pipe(
          map((response) =>
            attachmentsGetLatestResumeSuccess({
              payload: {
                ...response,
                candidateId: payload.candidateId,
              },
            }),
          ),
          catchError((error) => of(attachmentsGetLatestResumeFailure({ error }))),
        ),
      ),
    ),
  );

  loadLatestCoverLetter: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsGetLatestCoverLetter),
      pluck('payload'),
      distinct(
        (payload: { candidateId: number; onlyPdfFiles: boolean }) =>
          payload.candidateId + (payload.onlyPdfFiles ? '-onlyPdf' : '-all'),
      ),
      mergeMap((payload: { candidateId: number; onlyPdfFiles: boolean }) =>
        this.attachmentService.latestCoverLetter(payload.candidateId, payload.onlyPdfFiles).pipe(
          map((response) =>
            attachmentsGetLatestCoverLetterSuccess({
              payload: {
                ...response,
                candidateId: payload.candidateId,
              },
            }),
          ),
          catchError((error) => of(attachmentsGetLatestCoverLetterFailure({ error }))),
        ),
      ),
    ),
  );

  loadResumes: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsGetResumes),
      pluck('candidateId'),
      mergeMap((candidateId) =>
        this.attachmentService.resumes({ candidateId }).pipe(
          map((response) =>
            attachmentsGetResumesSuccess({
              payload: response.map((attachment) => ({
                ...attachment,
                candidateId,
              })),
            }),
          ),
          catchError((error) => of(attachmentsGetResumesFailure({ error }))),
        ),
      ),
    ),
  );

  getAttachment: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(attachmentsGet),
      mergeMap(({ id }) =>
        this.attachmentService.get({ id }).pipe(
          map((attachment) => attachmentsGetSuccess({ attachment })),
          catchError((error) => of(attachmentsGetFailure({ error }))),
        ),
      ),
    ),
  );

  constructor(private actions$: Actions, private attachmentService: AttachmentService) {}
}
